/**
 * Worlds Service - Fetches world data from API
 *
 * MERGES the hardcoded demo tours (worldsConfig.ts) with any active DB worlds
 * so that:
 *  - the two built-in demo tours never disappear (even if the API is empty/down)
 *  - activating a DB world is purely additive
 *  - if a DB world shares an id with a hardcoded one, the DB version wins
 */

import { WORLDS as HARDCODED_WORLDS } from '@/config/worldsConfig'
import type { World } from '@/config/worldsConfig'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export async function fetchWorlds(): Promise<World[]> {
  let dbWorlds: World[] = []
  try {
    const res = await fetch(`${API_URL}/api/worlds`)
    if (!res.ok) throw new Error('API error')
    const { data } = await res.json()
    if (Array.isArray(data)) dbWorlds = data
  } catch {
    // API unavailable — fall back to just the hardcoded demo tours
    return HARDCODED_WORLDS
  }

  // Merge: DB worlds win on id collision, otherwise both are shown.
  const byId = new Map<string, World>()
  for (const w of HARDCODED_WORLDS) byId.set(w.id, w)
  for (const w of dbWorlds) byId.set(w.id, w)
  return Array.from(byId.values())
}
